import {useSettingsPreviewMode} from '@common/admin/settings/preview/use-settings-preview-mode';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSettings} from '@ui/settings/use-settings';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import lazyLoader from '@ui/utils/loaders/lazy-loader';
import {useEffect} from 'react';
import {useLocation} from 'react-router';

const scriptTagId = 'hc-auto-livechat-loader';
const LOADER_VERSION = '2026-10-01-launcher-lift';

export function HcLivechatWidgetLoader() {
  const {base_url, hc, lc, chatWidget} = useSettings();
  const {trans} = useTrans();
  const {isInsideSettingsPreview} = useSettingsPreviewMode();
  const {pathname} = useLocation();

  /**
   * Support belongs on every page a visitor or student can reach — the old
   * allow-list of four prefixes meant the widget was missing from the
   * opportunities pages, the company portal, checkout and the account pages,
   * which is exactly where people get stuck.
   *
   * The rule is now a DENY-list: staff workspaces, where the chat panel would
   * only get in the way of someone answering it.
   */
  const isBackofficeRoute =
    pathname.startsWith('/admin') ||
    pathname.startsWith('/agent/') ||
    pathname.startsWith('/staff');
  // The standalone /livechat page IS the chat — a floating launcher on top of
  // it would open a second copy of itself.
  const isStandaloneLivechatPage = pathname.startsWith('/livechat');
  const shouldLoadOnAllPages = !!lc?.showOnAllPages;
  const shouldLoadLivechat =
    (shouldLoadOnAllPages || hc?.showLivechat) &&
    !isInsideSettingsPreview &&
    !isBackofficeRoute &&
    !isStandaloneLivechatPage;

  useEffect(() => {
    if ((!hc?.showLivechat && !shouldLoadOnAllPages) || isInsideSettingsPreview)
      return;

    // public/livechat-loader.js is served without cache headers, so browsers
    // kept an old copy after it changed. Bump this when editing that file.
    const url = `${base_url}/livechat-loader.js?v=${LOADER_VERSION}`;

    if (shouldLoadLivechat) {
      (window as any).BeChatSettings = {
        user: getBootstrapData().livechatWidgetUser,
        // The launcher button is built by livechat-loader.js, which runs
        // outside React and so has neither the translations nor the branding
        // settings. Hand it both: a translated label (it had none at all, so
        // screen readers announced an empty div) and the site's own mark.
        launcher: {
          label: trans(message('Chat with us')),
          // `chatWidget.color` is unset on this site, so the loader fell back
          // to a generic indigo that didn't match the rest of the UI. Use the
          // theme's actual primary colour instead.
          color: readPrimaryColour(),
        },
        // The launcher is now drawn at page load, before the iframe exists to
        // report position/spacing — so hand the loader the same config up front.
        widgetConfig: chatWidget,
      };
      lazyLoader.loadAsset(url, {type: 'js', id: scriptTagId});
    } else if ((window as any).BeChat) {
      (window as any).BeChat.destroy();
      lazyLoader.removeLoadedAsset(url);
      document.querySelector(`#${scriptTagId}`)?.remove();
      delete (window as any).BeChatSettings;
    }
  }, [pathname, isInsideSettingsPreview, hc?.showLivechat, shouldLoadOnAllPages]);

  return null;
}

/** `--be-primary` is an RGB triple ("83 66 238"), not a colour literal. */
function readPrimaryColour(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const triple = getComputedStyle(document.documentElement)
    .getPropertyValue('--be-primary')
    .trim();
  return triple ? `rgb(${triple})` : undefined;
}
